import { useEffect, useState } from 'react';
import { useTranslation } from '../../../i18n/useTranslation';
import { ApiError } from '../../../Models/ApiModel';
import type { OpenFormatSummaryModel } from '../../../Models/ReceiptModel';
import { ReceiptService } from '../../../Services/ReceiptService';
import { MoneyText } from '../../SharedArea/MoneyText/MoneyText';
import './OpenFormat.css';

/**
 * §2.6 of the uniform-structure instructions.
 *
 * The auditor holds this beside the file: how many documents of each type in
 * appendix 1 the period covers and for how much, with a zero against every
 * type the software does not manage — which is most of the twenty-seven. The
 * counts come from the same query the export runs, so the sheet in their hand
 * cannot disagree with the file on their screen.
 */
export function OpenFormatSummary({ from, to }: { from: string; to: string }) {
  const { tr } = useTranslation();
  const [report, setReport] = useState<OpenFormatSummaryModel | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // A new period invalidates what is on screen: showing February's counts
  // under March's dates is worse than showing nothing.
  useEffect(() => { setReport(null); setError(null); }, [from, to]);

  async function show() {
    setBusy(true);
    setError(null);
    try {
      setReport(await ReceiptService.openFormatSummary(from, to));
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : String(problem));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="openformat-summary">
      <p className="openformat-help">{tr('openFormatSummaryBody')}</p>
      <div className="openformat-summary-actions">
        <button type="button" className="ghost-button" onClick={show} disabled={busy}>
          {tr('openFormatSummaryShow')}
        </button>
        {report && (
          <button type="button" className="ghost-button" onClick={() => window.print()}>
            {tr('openFormatSummaryPrint')}
          </button>
        )}
      </div>
      {error && <p className="openformat-error">{error}</p>}
      {report && (
        <div className="openformat-report">
          <h3>{tr('openFormatSummaryTitle')}</h3>
          <p className="openformat-report-period">
            {tr('openFormatSummaryPeriod')}: <bdi>{report.from}</bdi> – <bdi>{report.to}</bdi>
          </p>
          <div className="openformat-report-scroll">
            <table className="openformat-report-table">
            <thead>
              <tr>
                <th>{tr('openFormatSummaryCode')}</th>
                <th>{tr('openFormatSummaryDocument')}</th>
                <th>{tr('openFormatSummaryCount')}</th>
                <th>{tr('openFormatSummaryTotal')}</th>
              </tr>
            </thead>
            <tbody>
              {report.documents.map(row => (
                <tr key={row.code} className={row.count === 0 ? 'is-empty' : undefined}>
                  <td><bdi>{row.code}</bdi></td>
                  <td>{row.name}</td>
                  <td><bdi>{row.count}</bdi></td>
                  <td><MoneyText agorot={row.totalAgorot} /></td>
                </tr>
              ))}
            </tbody>
            </table>
          </div>
          <p className="openformat-report-foot">
            {tr('openFormatSummaryCancelled')}: <bdi>{report.cancelledCount}</bdi>
            {' · '}
            {tr('openFormatSummaryRecords')}: <bdi>{report.totalRecords}</bdi>
            {' ('}
            {report.records.map(record => `${record.code} ${record.count}`).join(', ')}
            {')'}
          </p>
        </div>
      )}
    </div>
  );
}
